/**
 * Custom-column cell for `approval` columns. The value is
 * `{ state, reviewerId?, decidedAt? }`; the cell leads with the reviewer and
 * carries the verdict as a dot on their avatar, and opens a popover to name a
 * reviewer and record the decision.
 *
 * The decision is stored, not enforced — nothing downstream gates on it — so
 * the cell records who was asked and what they answered rather than pretending
 * to run an approval chain. Which is also why the reviewer comes first: the
 * question a board scan asks of a pending row is who it is sitting with.
 */
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Check, UserRoundCheck, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { parseLocalDate } from '../../lib/parseLocalDate';

import { useCurrentWorkspaceId } from '@/entities/workspace';
import { workspaceUsersQuery, type WorkspaceUser } from '@/entities/project-member';
import { getInitials } from '@/shared/utils/getInitials';

import { EM_DASH } from './constants';

/** Persisted shape of an `approval` cell value. */
export interface ApprovalValue {
  state: 'pending' | 'approved' | 'rejected';
  reviewerId?: string;
  decidedAt?: string;
}

interface ApprovalCustomCellProps {
  value: ApprovalValue | undefined;
  onChange: (next: ApprovalValue | null) => void;
  /** Shows the reviewer and verdict with nothing to change them, for someone who cannot. */
  readOnly?: boolean;
}

const DOT_CLASS_BY_STATE: Record<ApprovalValue['state'], string> = {
  pending: 'bg-warning',
  approved: 'bg-positive',
  rejected: 'bg-destructive',
};

export function ApprovalCustomCell({ value, onChange, readOnly }: ApprovalCustomCellProps) {
  const { t } = useTranslation('trackers');

  const [open, setOpen] = useState(false);
  const workspaceId = useCurrentWorkspaceId();

  const usersQuery = useQuery({
    ...workspaceUsersQuery(workspaceId),
    enabled: Boolean(workspaceId) && (open || value?.reviewerId !== undefined),
  });
  const users: WorkspaceUser[] = usersQuery.data?.data ?? [];
  const reviewer = users.find((u) => u.id === value?.reviewerId);

  /**
   * "No reviewer" is a fact about the row, so it is only said when the row
   * really names nobody. A reviewer we cannot resolve — the workspace list is
   * still in flight, or they have since left — reads as unknown instead.
   */
  const reviewerName = (approval: ApprovalValue): string => {
    if (reviewer) return reviewer.name;
    return approval.reviewerId === undefined
      ? t('trackersBoard.approvalCustomCell.noReviewer')
      : EM_DASH;
  };

  /**
   * The second line: what was decided and when. A decision saved before the
   * cell stamped `decidedAt`, or one carried over from an import, still has to
   * read as a verdict rather than as a blank line.
   */
  const decisionLine = (approval: ApprovalValue): string => {
    if (approval.state === 'pending') return t('trackersBoard.approvalCustomCell.awaitingReview');

    const decidedAt = parseLocalDate(approval.decidedAt);
    if (!decidedAt) {
      return approval.state === 'approved'
        ? t('trackersBoard.approvalCustomCell.approved')
        : t('trackersBoard.approvalCustomCell.rejected');
    }

    // eslint-disable-next-line i18next/no-literal-string -- a date-fns format pattern, not copy
    const date = format(decidedAt, 'd MMM');
    return approval.state === 'approved'
      ? t('trackersBoard.approvalCustomCell.approvedOn', { date })
      : t('trackersBoard.approvalCustomCell.rejectedOn', { date });
  };

  /**
   * Handing the row to a different person after someone else has answered
   * sends it back to pending: keeping the verdict would put one reviewer's
   * name against another's decision. Naming a reviewer on a decision that had
   * none is the opposite case — attributing it after the fact — so the verdict
   * stays.
   */
  const assignReviewer = (userId: string) => {
    const reassigned =
      value !== undefined &&
      value.state !== 'pending' &&
      value.reviewerId !== undefined &&
      value.reviewerId !== userId;

    onChange(
      reassigned
        ? { state: 'pending', reviewerId: userId }
        : { ...value, state: value?.state ?? 'pending', reviewerId: userId },
    );
  };

  const decide = (state: ApprovalValue['state']) => {
    onChange({
      state,
      ...(value?.reviewerId ? { reviewerId: value.reviewerId } : {}),
      ...(state === 'pending' ? {} : { decidedAt: new Date().toISOString() }),
    });
    setOpen(false);
  };

  const display = value ? (
    <span className="flex w-full min-w-0 items-center gap-2">
      <span className="relative shrink-0">
        <Avatar className="size-6">
          {reviewer?.avatarUrl ? (
            <AvatarImage src={reviewer.avatarUrl} alt={reviewer.name} />
          ) : null}
          <AvatarFallback className="text-[10px]">
            {reviewer ? (
              getInitials(reviewer.name)
            ) : (
              <UserRoundCheck className="size-3" aria-hidden />
            )}
          </AvatarFallback>
        </Avatar>
        <span
          className={`absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2 border-background ${DOT_CLASS_BY_STATE[value.state]}`}
          aria-hidden
        />
      </span>
      <span className="flex min-w-0 flex-col text-left leading-tight">
        <span className="truncate text-xs font-medium text-foreground">{reviewerName(value)}</span>
        <span className="truncate text-[11px] text-muted-foreground">{decisionLine(value)}</span>
      </span>
    </span>
  ) : readOnly ? (
    <span className="text-muted-foreground">{EM_DASH}</span>
  ) : (
    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <UserRoundCheck className="size-3.5 shrink-0" aria-hidden />
      {t('trackersBoard.approvalCustomCell.requestApproval')}
    </span>
  );

  if (readOnly) {
    return <div className="flex w-full items-center justify-center">{display}</div>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="flex w-full items-center justify-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {display}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2" onClick={(e) => e.stopPropagation()}>
        <p className="px-1 pb-1 text-xs font-medium text-muted-foreground">
          {t('trackersBoard.approvalCustomCell.reviewer')}
        </p>
        <ul className="mb-2 flex max-h-40 flex-col gap-0.5 overflow-y-auto">
          {users.map((user) => (
            <li key={user.id}>
              <button
                type="button"
                onClick={() => assignReviewer(user.id)}
                className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-xs transition-colors hover:bg-accent"
              >
                <Avatar className="size-6 shrink-0">
                  {user.avatarUrl ? <AvatarImage src={user.avatarUrl} alt={user.name} /> : null}
                  <AvatarFallback className="text-[10px]">{getInitials(user.name)}</AvatarFallback>
                </Avatar>
                <span className="truncate">{user.name}</span>
                {user.id === value?.reviewerId ? (
                  <Check className="ml-auto size-3.5 shrink-0 text-primary" aria-hidden />
                ) : null}
              </button>
            </li>
          ))}
        </ul>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => decide('approved')}
            className="flex flex-1 items-center justify-center gap-1 rounded bg-positive px-2 py-1 text-xs font-medium text-positive-foreground hover:opacity-80"
          >
            <Check className="size-3" aria-hidden />
            {t('trackersBoard.approvalCustomCell.approve')}
          </button>
          <button
            type="button"
            onClick={() => decide('rejected')}
            className="flex flex-1 items-center justify-center gap-1 rounded bg-destructive/15 px-2 py-1 text-xs font-medium text-destructive hover:opacity-80"
          >
            <X className="size-3" aria-hidden />
            {t('trackersBoard.approvalCustomCell.reject')}
          </button>
        </div>
        {value ? (
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
            className="mt-1 w-full rounded px-2 py-1 text-left text-xs text-muted-foreground transition-colors hover:bg-accent"
          >
            {t('trackersBoard.approvalCustomCell.clear')}
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
